import { Card, CardBooleanProperty, CardNumberProperty, CardStringArrayProperty, CardStringProperty } from "../models/Card";
import { MathComparisonSymbol } from "../models/Math";
import { AnyCardRuleType, CardRuleCategory, CardTextRuleType, MATH_COMPARISON_SYMBOL_TEXT_MAP } from "./CardRuleEngine";

export type Archetype = {
    id: string | undefined,
    name: string,
    rules: AnyArchetypeRule[]
}

export type AnyArchetypeRule = ArchetypeCardStringRule | ArchetypeCardStringArrayRule | ArchetypeCardNumberRule | ArchetypeCardTextRule | ArchetypeCompositeRule | ArchetypeCardBooleanRule;

export type ArchetypeRule<TCategory extends CardRuleCategory, TType extends string> = {
    type: TType,
    category: TCategory,
    quantity: number,
    quantityComparisonSymbol: MathComparisonSymbol
}

export type ArchetypeSimpleRule<TCategory extends CardRuleCategory, TType extends string> = {
    type: TType,
    category: TCategory,
    value: TCategory extends "CardNumberRule" ? number : TCategory extends "CardBooleanRule" ? boolean : string;
    valueComparisonSymbol: TCategory extends "CardNumberRule" ? MathComparisonSymbol : undefined
}

export type ArchetypeCardTextRule = ArchetypeRule<"CardTextRule", CardTextRuleType>;
export type ArchetypeCardStringRule = ArchetypeRule<"CardStringRule", CardStringProperty> & { value: string };
export type ArchetypeCardStringArrayRule = ArchetypeRule<"CardStringArrayRule", CardStringArrayProperty> & { value: string };
export type ArchetypeCardNumberRule = ArchetypeRule<"CardNumberRule", CardNumberProperty> & { value: number, valueComparisonSymbol: MathComparisonSymbol };
export type ArchetypeCardBooleanRule = ArchetypeRule<"CardBooleanRule", CardBooleanProperty> & { value: boolean };
export type ArchetypeCompositeRule = ArchetypeRule<"CompositeRule", "composite"> & { rules: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>[], matchMethod: "and" | "or" }

export type RuleResult = {
    expectedQuantity: number,
    expectedQuantityComparisonSymbol: MathComparisonSymbol,
    actualQuantity: number
}

// Helper constant. Only exists to build a list of strings from other types.
export const CARD_RULE_EXPLAINATION_MAP: Record<AnyCardRuleType, (rule: AnyArchetypeRule) => string> = {
    categories: (rule: AnyArchetypeRule) => {
        const convertedRule = rule as ArchetypeCardStringArrayRule;
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        return `${symbolText}${convertedRule.quantity} category ${convertedRule.value} cards.`;
    },
    cost: (rule: AnyArchetypeRule) => {
        const convertedRule = rule as ArchetypeCardNumberRule;
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        const valueSymbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[convertedRule.valueComparisonSymbol];

        if (convertedRule.valueComparisonSymbol === "=") {
            return `${symbolText}${convertedRule.quantity} cost ${convertedRule.value} cards.`;
        }

        return `${symbolText}${convertedRule.quantity} cards with a cost ${valueSymbolText.toLocaleLowerCase()} ${convertedRule.value}.`;
    },
    elements: (rule: AnyArchetypeRule) => {
        const convertedRule = rule as ArchetypeCardStringArrayRule;
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        return `${symbolText}${convertedRule.quantity} ${convertedRule.value} cards.`;
    },
    hasDamageXAbility: (rule: AnyArchetypeRule) => {
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        return `${symbolText}${rule.quantity} cards with a Damage X ability.`;
    },
    isCrystalGenerator: (rule: AnyArchetypeRule) => {
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        return `${symbolText}${rule.quantity} cards with a crystal generator.`;
    },
    jobs: (rule: AnyArchetypeRule) => {
        const convertedRule = rule as ArchetypeCardStringArrayRule;
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        return `${symbolText}${convertedRule.quantity} job ${convertedRule.value} cards.`;
    },
    name: (rule: AnyArchetypeRule) => {
        const convertedRule = rule as ArchetypeCardStringRule;
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        return `${symbolText}${convertedRule.quantity} name ${convertedRule.value} cards.`;
    },
    number: (rule: AnyArchetypeRule) => {
        return "";
    },
    opus: (rule: AnyArchetypeRule) => {
        return "";
    },
    power: (rule: AnyArchetypeRule) => {
        const convertedRule = rule as ArchetypeCardNumberRule;
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        const valueSymbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[convertedRule.valueComparisonSymbol];

        if (convertedRule.valueComparisonSymbol === "=") {
            return `${symbolText}${convertedRule.quantity} power ${convertedRule.value} cards.`;
        }

        return `${symbolText}${convertedRule.quantity} cards with a power ${valueSymbolText.toLocaleLowerCase()} ${convertedRule.value}.`;
    },
    rarity: (rule: AnyArchetypeRule) => {
        const convertedRule = rule as ArchetypeCardStringArrayRule;
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        return `${symbolText}${convertedRule.quantity} ${convertedRule.value} rarity cards.`;
    },
    serial: (rule: AnyArchetypeRule) => {
        const convertedRule = rule as ArchetypeCardStringArrayRule;
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        return `${symbolText}${convertedRule.quantity} cards with serial ${convertedRule.value}.`;
    },
    text: (rule: AnyArchetypeRule) => {
        return "";
    },
    type: (rule: AnyArchetypeRule) => {
        const convertedRule = rule as ArchetypeCardStringArrayRule;
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        return `${symbolText}${convertedRule.quantity} ${convertedRule.value} cards.`;
    },
    isExBurst: (rule: AnyArchetypeRule) => {
        const convertedRule = rule as ArchetypeCardBooleanRule;
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        return `${symbolText}${convertedRule.quantity} cards ${convertedRule.value ? "with" : "without"} EX Burst.`;
    },
    isLB: (rule: AnyArchetypeRule) => {
        const convertedRule = rule as ArchetypeCardBooleanRule;
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        return `${symbolText}${convertedRule.quantity} cards ${convertedRule.value ? "with" : "without"} Limit Break.`;
    },
    isMultiPlayable: (rule: AnyArchetypeRule) => {
        const convertedRule = rule as ArchetypeCardBooleanRule;
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        return `${symbolText}${convertedRule.quantity} cards ${convertedRule.value ? "with" : "without"} Multiplay.`;
    },
    isSpecial: (rule: AnyArchetypeRule) => {
        const convertedRule = rule as ArchetypeCardBooleanRule;
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        return `${symbolText}${convertedRule.quantity} cards ${convertedRule.value ? "with" : "without"} a special.`;
    },
    isMultiElement: (rule: AnyArchetypeRule) => {
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        return `${symbolText}${rule.quantity} multi element cards.`;
    },
    composite: (rule: AnyArchetypeRule) => {
        const convertedRule = rule as ArchetypeCompositeRule;
        const symbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.quantityComparisonSymbol];
        let result = `${symbolText}${rule.quantity} cards that fulfill ${ convertedRule.matchMethod === "and" ? "all" : "some" } of the following:`;

        convertedRule.rules.forEach((x) => {
            const getLine = COMPOSITE_RULE_EXPLAINATION_MAP[x.type];
            if (!getLine) {
                return;
            }

            const line = getLine(x);
            if (!line) {
                return;
            }

            result = `${result}\n- ${line}`
        });

        return result;
    }
}

export const COMPOSITE_RULE_EXPLAINATION_MAP: Record<AnyCardRuleType, (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => string> = {
    categories: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return `Category ${rule.value}.`;
    },
    cost: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        const valueSymbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.valueComparisonSymbol ?? "="];

        if (rule.valueComparisonSymbol === "=") {
            return `Cost ${rule.value}.`;
        }

        return `Cost ${valueSymbolText.toLocaleLowerCase()} ${rule.value}.`;
    },
    elements: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return `${rule.value} element.`;
    },
    hasDamageXAbility: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return `Damage X ability.`;
    },
    isCrystalGenerator: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return `Crystal generator.`;
    },
    jobs: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return `Job ${rule.value}.`;
    },
    name: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return `Name ${rule.value}.`;
    },
    number: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return "";
    },
    opus: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return "";
    },
    power: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        const valueSymbolText = MATH_COMPARISON_SYMBOL_TEXT_MAP[rule.valueComparisonSymbol ?? "="];

        if (rule.valueComparisonSymbol === "=") {
            return `Power ${rule.value}.`;
        }

        return `Power ${valueSymbolText.toLocaleLowerCase()} ${rule.value}.`;
    },
    rarity: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return `Rarity ${rule.value}.`;
    },
    serial: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return `Serial ${rule.value}.`;
    },
    text: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return "";
    },
    type: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return `${rule.value}.`;
    },
    isExBurst: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return `${rule.value ? "With" : "Without"} EX Burst.`;
    },
    isLB: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return `${rule.value ? "With" : "Without"} Limit Break.`;
    },
    isMultiPlayable: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return `${rule.value ? "With" : "Without"} Multiplay.`;
    },
    isSpecial: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return `${rule.value ? "With" : "Without"} special.`;
    },
    isMultiElement: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return `${rule.value ? "With" : "Without"} multi element.`;
    },
    composite: (rule: ArchetypeSimpleRule<CardRuleCategory, AnyCardRuleType>) => {
        return "";
    }
}

export type ArchetypeName = {
    id: string,
    name: string
}